import { Button, Form, Input, InputNumber, Select } from 'antd';
import { Plus, Trash2 } from 'lucide-react';
import {
  MAX_ODOMETER_KM,
  MAX_POST_TRIP_DEFECTS,
  MAX_POST_TRIP_TEXT_LENGTH,
  POST_TRIP_BODY_DAMAGES,
  POST_TRIP_DEFECT_AREAS,
  POST_TRIP_DEFECT_SEVERITIES,
  POST_TRIP_TYRE_CONDITIONS,
} from '@chukua/common';

const toOptions = (values: readonly string[]) => values.map((value) => ({ value, label: value[0].toUpperCase() + value.slice(1) }));
const TYRE_OPTIONS = toOptions(POST_TRIP_TYRE_CONDITIONS);
const BODY_OPTIONS = toOptions(POST_TRIP_BODY_DAMAGES);
const AREA_OPTIONS = toOptions(POST_TRIP_DEFECT_AREAS);
const SEVERITY_OPTIONS = toOptions(POST_TRIP_DEFECT_SEVERITIES);

/**
 * What the driver reads off the truck on return (gitaumoses4/chukua#77): odometer and fuel gauge are required, as the server requires them;
 * the conditions and the defects are optional. A damaged tyre is also entered as a defect in the tyre area.
 */
export function DriverCapturePostTripFields() {
  return (
    <>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Form.Item name="odometerKm" label="Odometer (km)" rules={[{ required: true, message: 'Enter the odometer reading' }]}>
          <InputNumber className="w-full" min={0} max={MAX_ODOMETER_KM} precision={0} controls={false} />
        </Form.Item>
        <Form.Item name="fuelLevelPercent" label="Fuel level (%)" rules={[{ required: true, message: 'Enter the fuel level' }]}>
          <InputNumber className="w-full" min={0} max={100} precision={0} controls={false} />
        </Form.Item>
      </div>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Form.Item name="tyreCondition" label="Tyres">
          <Select allowClear options={TYRE_OPTIONS} placeholder="Not checked" />
        </Form.Item>
        <Form.Item name="bodyDamage" label="Body damage">
          <Select allowClear options={BODY_OPTIONS} placeholder="Not checked" />
        </Form.Item>
      </div>
      <Form.List name="defects">
        {(fields, { add, remove }) => (
          <div className="mb-4 flex flex-col gap-2">
            {fields.map((field) => (
              <div key={field.key} className="grid items-start gap-x-3 sm:grid-cols-[8rem_8rem_1fr_auto]">
                <Form.Item name={[field.name, 'area']} label="Defect area" rules={[{ required: true, message: 'Choose the area' }]} className="mb-0">
                  <Select options={AREA_OPTIONS} />
                </Form.Item>
                <Form.Item name={[field.name, 'severity']} label="Severity" rules={[{ required: true, message: 'Choose the severity' }]} className="mb-0">
                  <Select options={SEVERITY_OPTIONS} />
                </Form.Item>
                <Form.Item
                  name={[field.name, 'description']}
                  label="What is wrong"
                  rules={[{ required: true, whitespace: true, message: 'Say what is wrong' }, { max: MAX_POST_TRIP_TEXT_LENGTH }]}
                  className="mb-0"
                >
                  <Input />
                </Form.Item>
                <Button
                  className="sm:mt-[30px]"
                  aria-label="Remove defect"
                  icon={<Trash2 className="size-4" aria-hidden />}
                  onClick={() => remove(field.name)}
                />
              </div>
            ))}
            {fields.length < MAX_POST_TRIP_DEFECTS && (
              <div>
                <Button icon={<Plus className="size-4" aria-hidden />} onClick={() => add()}>
                  Add defect
                </Button>
              </div>
            )}
          </div>
        )}
      </Form.List>
    </>
  );
}
